import { z } from 'zod';
import type { ActivitySubjectType } from '../activity/activities.schemas';

/**
 * Link metadata on get-by-id responses (IMM-569.8).
 *
 * Every documented get-by-id response carries a `links` object: `self`, plus the
 * URL of each related resource. An integrator holding one record follows those
 * URLs instead of reassembling paths from a table in the reference and guessing
 * which scope applies.
 *
 * Four rules, pinned by the parent PRD's `## Fields` table and enforced by the
 * builders below rather than restated at each call site:
 *
 *  1. **`self` is the URL of the endpoint that produced the response**, echoing
 *     the route the caller used — never a canonical alternative. A deal is
 *     published under two detail routes; each returns its own `self`, so a client
 *     comparing two `self` values must not treat them as different records. The
 *     shared response mappers cannot know their own route (they also serve every
 *     mutation), so the CALLER supplies `selfUrl`.
 *  2. **A missing single record has no key at all** — omitted, never `null` and
 *     never a URL that would answer as missing. A `null` would force every
 *     consumer to null-check a key whose absence already carries the same
 *     meaning.
 *  3. **A collection link is always present**, even when the collection is empty:
 *     an empty collection is a valid resource, not a missing one.
 *  4. **A link is not an authorisation claim.** Its presence says the resource
 *     exists; whether this key may read it is answered by the request. Several
 *     targets need a different grant from the response that carried the link, and
 *     the reference names that grant beside each one.
 *
 * Every value is a path from the API root, in the same form the deal
 * `detailUrl` has always used — one URL convention, not two.
 */

/** A path from the API root, e.g. `/v1/organizations/{organizationId}/deals/{id}`. */
const LinkSchema = z.string();

export const PropertyLinksSchema = z.object({
  self: LinkSchema,
  activities: LinkSchema,
  documents: LinkSchema,
  media: LinkSchema,
  dossier: LinkSchema,
  valuation: LinkSchema,
});
export type PropertyLinks = z.infer<typeof PropertyLinksSchema>;

export const DealLinksSchema = z.object({
  self: LinkSchema,
  property: LinkSchema,
  activities: LinkSchema,
  phaseHistory: LinkSchema,
  valuationRequest: LinkSchema,
});
export type DealLinks = z.infer<typeof DealLinksSchema>;

export const LeadLinksSchema = z.object({
  self: LinkSchema,
  // Every other lead link is ORGANISATION-addressed, and a lead's location is
  // optional — a lead captured without one has no organisation-addressed URL to
  // offer, so the key is omitted rather than pointing somewhere unresolvable.
  // Present for every lead that carries a location, empty timeline or not.
  activities: LinkSchema.optional(),
  // Omitted — not null — while the lead is unqualified: it has converted into
  // nothing, so there is no contact, property or deal to point at.
  convertedContact: LinkSchema.optional(),
  convertedProperty: LinkSchema.optional(),
  convertedDeal: LinkSchema.optional(),
});
export type LeadLinks = z.infer<typeof LeadLinksSchema>;

export const ValuationRequestLinksSchema = z.object({
  self: LinkSchema,
  deal: LinkSchema,
  // Both resolve off the request the response carries, so both are omitted for a
  // deal nobody has asked a valuation for — there is no request to file
  // attachments under and no property to point at.
  property: LinkSchema.optional(),
  documents: LinkSchema.optional(),
  // Omitted when the request holds no report the caller can read — including
  // while the valuation waits for release, when the report set is withheld.
  report: LinkSchema.optional(),
});
export type ValuationRequestLinks = z.infer<typeof ValuationRequestLinksSchema>;

const organizationBase = (organizationId: string): string => `/v1/organizations/${organizationId}`;

/**
 * The activity timeline of one subject. The activities read takes the subject as
 * query parameters rather than in the path, so the link carries them — a
 * consumer follows it verbatim and pages with `?cursor=`.
 */
export function activitiesLink(organizationId: string, subjectType: ActivitySubjectType, subjectId: string): string {
  return `${organizationBase(organizationId)}/activities?subjectType=${subjectType}&subjectId=${subjectId}`;
}

/** The organisation-addressed detail URL of a property — the default `self`. */
export function propertyDetailUrl(organizationId: string, propertyId: string): string {
  return `${organizationBase(organizationId)}/properties/${propertyId}`;
}

/** The organisation-addressed detail URL of a deal — the value of `detailUrl`. */
export function dealDetailUrl(organizationId: string, dealId: string): string {
  return `${organizationBase(organizationId)}/deals/${dealId}`;
}

/**
 * The organisation-addressed URL of one valuation request's report — the target
 * of `links.report` wherever a caller may read one. Exported so the two builders
 * that publish it (the Deal's `valuationRequestLinks` below, and the member's
 * workspace-addressed detail read) write the path once instead of twice.
 */
export function valuationReportUrl(organizationId: string, requestId: string): string {
  return `${organizationBase(organizationId)}/valuation-request/${requestId}/report`;
}

/** The workspace-addressed detail URL of a lead — the only lead detail route. */
export function leadDetailUrl(workspaceId: string, leadId: string): string {
  return `/v1/workspaces/${workspaceId}/leads/${leadId}`;
}

/**
 * `links` for a property. Every organisation-addressed target is built from the
 * organisation the response already carries, NOT from the route's path, so the
 * workspace-addressed detail read still emits usable links. All five related
 * targets always exist — the documents and gallery reads answer 200 with an
 * empty list, the dossier and valuation reads answer 200 with an empty record —
 * so no key is ever conditional here.
 */
export function propertyLinks(input: {
  readonly organizationId: string;
  readonly propertyId: string;
  readonly selfUrl: string;
}): PropertyLinks {
  const base = propertyDetailUrl(input.organizationId, input.propertyId);

  return {
    self: input.selfUrl,
    activities: activitiesLink(input.organizationId, 'PROPERTY', input.propertyId),
    documents: `${base}/documents`,
    media: `${base}/media`,
    dossier: `${base}/dossier`,
    valuation: `${base}/valuation`,
  };
}

/**
 * `links` for a deal. A deal always has a property and both the phase history
 * and the valuation-request reads answer for one that has never moved or never
 * been valued, so nothing here is conditional.
 */
export function dealLinks(input: {
  readonly organizationId: string;
  readonly dealId: string;
  readonly propertyId: string;
  readonly selfUrl: string;
}): DealLinks {
  const base = dealDetailUrl(input.organizationId, input.dealId);

  return {
    self: input.selfUrl,
    property: propertyDetailUrl(input.organizationId, input.propertyId),
    activities: activitiesLink(input.organizationId, 'DEAL', input.dealId),
    phaseHistory: `${base}/phase-history`,
    valuationRequest: `${base}/valuation-request`,
  };
}

/**
 * `links` for a lead. The converted trio appears only once the lead has been
 * qualified — spread conditionally so an unqualified lead carries no key at all
 * rather than three nulls.
 *
 * A lead's location is optional (`organisationId` is nullable on the record), and
 * every link but `self` is organisation-addressed. A lead captured without a
 * location therefore carries `self` alone: there is no organisation to address
 * its timeline — or the records it converted into — under.
 */
export function leadLinks(input: {
  readonly organisationId: string | null;
  readonly leadId: string;
  readonly selfUrl: string;
  readonly convertedContactId: string | null;
  readonly convertedPropertyId: string | null;
  readonly convertedDealId: string | null;
}): LeadLinks {
  const organisationId = input.organisationId;

  if (organisationId === null) return { self: input.selfUrl };

  return {
    self: input.selfUrl,
    activities: activitiesLink(organisationId, 'LEAD', input.leadId),
    ...(input.convertedContactId === null
      ? {}
      : { convertedContact: `${organizationBase(organisationId)}/contacts/${input.convertedContactId}` }),
    ...(input.convertedPropertyId === null
      ? {}
      : { convertedProperty: propertyDetailUrl(organisationId, input.convertedPropertyId) }),
    ...(input.convertedDealId === null ? {} : { convertedDeal: dealDetailUrl(organisationId, input.convertedDealId) }),
  };
}

/**
 * `links` for the deal's valuation. `self` is the deal-addressed read that
 * produced the response and `deal` the deal it hangs off — both always known
 * from the route. The other three hang off the request itself: `property` and
 * `documents` need a request to exist, and `report` needs that request to hold a
 * report this caller can read (the report set is withheld while the valuation
 * waits for release, and a link to it would 404).
 */
export function valuationRequestLinks(input: {
  readonly organizationId: string;
  readonly dealId: string;
  readonly requestId: string | null;
  readonly propertyId: string | null;
  readonly hasReadableReport: boolean;
}): ValuationRequestLinks {
  const base = organizationBase(input.organizationId);
  const requestId = input.requestId;
  const requestBase = requestId === null ? null : `${base}/valuation-request/${requestId}`;

  return {
    self: `${dealDetailUrl(input.organizationId, input.dealId)}/valuation-request`,
    deal: dealDetailUrl(input.organizationId, input.dealId),
    ...(input.propertyId === null ? {} : { property: propertyDetailUrl(input.organizationId, input.propertyId) }),
    ...(requestBase === null ? {} : { documents: `${requestBase}/documents` }),
    ...(requestId !== null && input.hasReadableReport
      ? { report: valuationReportUrl(input.organizationId, requestId) }
      : {}),
  };
}
